import isEmpty from 'lodash-es/isEmpty';
import { Fragment } from 'react';
import { SelectionCardContent } from '@components/SelectionCard';
import { css } from '@emotion/css';
import { OpenBankingAccountStatusReason } from '@graphql/index';
import { Box } from '@spark-web/box';
import { Divider } from '@spark-web/divider';
import { CheckCircleIcon, ExclamationIcon } from '@spark-web/icon';
import { Inline } from '@spark-web/inline';
import { Row } from '@spark-web/row';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useTheme } from '@spark-web/theme';
import { formatDollarsWithoutCents } from '@steps/FinancePurchase/utils/getFinanceDetails';
import { isJointApplicant } from '@utils';
import { BankIncomeSource, BankIncomeSources } from './hooks';

interface BankAccountsListProps {
  bankIncomeSources: BankIncomeSources;
}

export const BankAccountsList = ({ bankIncomeSources }: BankAccountsListProps) => {
  const theme = useTheme();
  const isJoint = isJointApplicant();

  const getStatusConfig = (
    account: BankIncomeSource,
  ): {
    icon: JSX.Element;
    text: string;
    tone: 'neutral' | 'caution' | undefined;
  } => {
    if (account.statusReason === OpenBankingAccountStatusReason.BUSINESS_ACCOUNT) {
      return {
        icon: <ExclamationIcon tone="secondary" size="xxsmall" />,
        text: `Account ${account.maskedAccountNumber} (Business account)`,
        tone: 'caution',
      };
    }

    if (account.statusReason === OpenBankingAccountStatusReason.REPLICATED_PRIMARY && isJoint) {
      return {
        icon: <ExclamationIcon tone="secondary" size="xxsmall" />,
        text: `Account ${account.maskedAccountNumber} (Already assessed)`,
        tone: 'caution',
      };
    }

    if (!account?.totalMonthlyIncome) {
      return {
        icon: <ExclamationIcon tone="secondary" size="xxsmall" />,
        text: `Account ${account.maskedAccountNumber} (No income found)`,
        tone: 'caution',
      };
    }

    return {
      icon: <CheckCircleIcon tone="primary" size="xxsmall" />,
      text: `Account ${account.maskedAccountNumber}`,
      tone: 'neutral',
    };
  };

  if (!bankIncomeSources || isEmpty(bankIncomeSources)) {
    return null;
  }

  return (
    <Stack padding="large" border="standard" borderRadius="small" gap="large" background="surfaceMuted">
      <Text size="small" tone="muted" weight="semibold">
        CONNECTED ACCOUNTS
      </Text>
      {Object.entries(bankIncomeSources).map(([bank, accounts]) => (
        <Fragment key={bank}>
          <Divider />
          <Row key={bank} gap="medium">
            <img
              src={accounts?.[0]?.institutionLogoUrl}
              height={40}
              width={40}
              alt="Open Banking Bank Logo"
              className={css({ borderRadius: '8px', background: theme.color.background.infoMuted })}
            />
            <SelectionCardContent>
              <Text weight="semibold" size="small">
                {accounts?.[0]?.institutionName}
              </Text>
              {accounts?.map((account: BankIncomeSource) => {
                const config = getStatusConfig(account);
                return (
                  <Box key={account.accountId} display="flex" alignItems="center" justifyContent="spaceBetween">
                    <Row alignY="center" gap="small">
                      {config.icon}
                      <Text size="small" tone={config.tone}>
                        {config.text}
                      </Text>
                    </Row>
                    <Inline gap="xsmall" align="right">
                      <Text weight="semibold">{formatDollarsWithoutCents(account.totalMonthlyIncome ?? 0)}</Text>
                      <Text>monthly</Text>
                    </Inline>
                  </Box>
                );
              })}
            </SelectionCardContent>
          </Row>
        </Fragment>
      ))}
    </Stack>
  );
};
